<script setup lang="ts">
import { ref, watch } from 'vue'

import MarkdownPreviewDialog from '@/components/molecules/markdown/MarkdownPreviewDialog.vue'
import {
  KNOWLEDGE_PROCESSING_STATUSES,
  KNOWLEDGE_STATUS_COLOR_MAP,
  KNOWLEDGE_STATUS_LABEL_MAP,
} from '@/composables/ai/useCampaignKnowledge'
import {
  useCampaignService,
  type ICampaignKnowledgeDocument,
  type ICampaignKnowledgeDocumentDetail,
} from '@/services/campaign/useCampaignService'

const props = defineProps<{
  campaignId?: string | null
}>()

const visible = defineModel<boolean>({ default: false })

const campaignService = useCampaignService()
const selectedDocument = ref<ICampaignKnowledgeDocument | null>(null)
const selectedDocumentDetail = ref<ICampaignKnowledgeDocumentDetail | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
let polling: number | null = null

const stopPolling = () => {
  if (polling !== null) {
    window.clearInterval(polling)
    polling = null
  }
}

const loadDocument = async (document: ICampaignKnowledgeDocument) => {
  if (!props.campaignId) return

  loading.value = true
  error.value = null
  try {
    const response = await campaignService.getCampaignKnowledgeDocument(props.campaignId, document.id)
    selectedDocumentDetail.value = response
    selectedDocument.value = response
    if (!KNOWLEDGE_PROCESSING_STATUSES.includes(response.status)) {
      stopPolling()
    }
  } catch {
    error.value = 'No se pudo cargar el documento.'
    stopPolling()
  } finally {
    loading.value = false
  }
}

const openDocument = (document: ICampaignKnowledgeDocument) => {
  selectedDocument.value = document
  selectedDocumentDetail.value = null
  error.value = null
  visible.value = true
  stopPolling()
  void loadDocument(document)

  if (KNOWLEDGE_PROCESSING_STATUSES.includes(document.status)) {
    polling = window.setInterval(() => {
      const currentDocument = selectedDocument.value
      if (!currentDocument || !visible.value) {
        stopPolling()
        return
      }
      void loadDocument(currentDocument)
    }, 3000)
  }
}

const copyContent = async () => {
  const content = selectedDocumentDetail.value?.content
  if (!content) return
  await navigator.clipboard.writeText(content)
}

defineExpose({
  openDocument,
})

watch(visible, (isVisible) => {
  if (!isVisible) stopPolling()
})
</script>

<template>
  <MarkdownPreviewDialog
    v-model="visible"
    :title="selectedDocument?.title || 'Base de conocimiento'"
    :status="selectedDocumentDetail?.status || selectedDocument?.status || ''"
    :content="selectedDocumentDetail?.status === 'ready' ? selectedDocumentDetail.content : ''"
    :loading="loading"
    :error="error"
    :status-color-map="KNOWLEDGE_STATUS_COLOR_MAP"
    :status-label-map="KNOWLEDGE_STATUS_LABEL_MAP"
    @copy="copyContent"
  />
</template>
